import { Component, Input } from '@angular/core';
import {
    ComponentTypeDto,
    CraftService,
    EquipmentModelService,
    EquipmentService,
    ResourceComponentInServiceTime,
    RpaTypesService,
    TotalInServiceTime,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface DisplayableComponentType {
    componentTypeName: string;
    components: ResourceComponentInServiceTime[];
}

function sortComponents(
    a: ResourceComponentInServiceTime,
    b: ResourceComponentInServiceTime
): number {
    const componentTypeOrder = a.componentType.name.localeCompare(
        b.componentType.name
    );
    if (componentTypeOrder !== 0) {
        return componentTypeOrder;
    }
    return a.designation.localeCompare(b.designation);
}

@Component({
    selector: 'component-service-time',
    templateUrl: './component-service-time.component.html'
})
export class ComponentServiceTime {
    @Input() resourceId: number;
    @Input() resourceType: 'CRAFT' | 'EQUIPMENT';
    @Input() managingOrganisationId: number;
    @Input() hasScheduledMaintenance = false;
    @Input() timeInServiceMode: 'TOTAL' | 'MAINTENANCE' = 'TOTAL';
    @Input() until?: string;

    serviceTimes: TotalInServiceTime;
    tableComponents: DisplayableComponentType[] = [];

    diagramUrl: string;

    working: boolean;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private craftService: CraftService,
        private rpaTypesService: RpaTypesService,
        private equipmentService: EquipmentService,
        private equipmentTypeService: EquipmentModelService
    ) {}

    ngOnInit() {
        if (this.resourceType === 'CRAFT') {
            this.craftService.change$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.refreshServiceTimes());
        }
        if (this.resourceType === 'EQUIPMENT') {
            this.equipmentService.change$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(() => this.refreshServiceTimes());
        }
        this.refreshServiceTimes();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshServiceTimes() {
        if (this.resourceType === 'CRAFT') {
            this.craftService
                .findTotalTimeInService(
                    this.resourceId,
                    this.managingOrganisationId,
                    this.until
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(total => {
                    this.serviceTimes = {
                        ...total,
                        components: [...total.components].sort(sortComponents)
                    };
                    this.buildDisplayableComponentTypes();
                })
                .add(this.workTracker.createTracker());
        }
        if (this.resourceType === 'EQUIPMENT') {
            this.equipmentService
                .findTotalTimeInService(
                    this.resourceId,
                    this.managingOrganisationId
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(total => {
                    this.serviceTimes = {
                        ...total,
                        components: [...total.components].sort(sortComponents)
                    };
                    this.buildDisplayableComponentTypes();
                })
                .add(this.workTracker.createTracker());
        }
    }

    buildDisplayableComponentTypes() {
        const types = this.serviceTimes.components.reduce(
            (acc: string[], c) =>
                acc.includes(c.componentType.name)
                    ? acc
                    : [...acc, c.componentType.name],
            []
        );

        this.tableComponents = types.reduce(
            (acc: DisplayableComponentType[], type) =>
                acc.concat({
                    componentTypeName: type,
                    components: this.serviceTimes.components
                        .filter(
                            component => component.componentType.name === type
                        )
                        .sort(sortComponents)
                }),
            []
        );
    }

    getAttachmentUrl(component: ResourceComponentInServiceTime) {
        this.diagramUrl = null;
        // TODO: this call can move to one service since it's not type-specific
        if (this.resourceType === 'CRAFT') {
            this.diagramUrl = this.rpaTypesService.getSchematicAttachmentUrl(
                this.serviceTimes.schematicId,
                component.attachmentId
            );
        }
        if (this.resourceType === 'EQUIPMENT') {
            this.diagramUrl =
                this.equipmentTypeService.getSchematicAttachmentUrl(
                    this.serviceTimes.schematicId,
                    component.attachmentId
                );
        }
    }

    findComponentIcon(component: ResourceComponentInServiceTime) {
        if (
            component.componentType.iconFamily !==
                ComponentTypeDto.IconFamily.FONT_AWESOME ||
            component.componentType.icon == null
        ) {
            return null;
        }
        const icon = `right-buffer fal fa-${component.componentType.icon}`;
        return icon;
    }
}
